import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { APP_MODES } from '../hooks/subdomainConfig.js';
import { Link } from 'react-router-dom';
import PartnerManagement from '../components/PartnerManagement.jsx';
import AdminContactsManagement from '../components/AdminContactsManagement.jsx';
import API_URL from '../config/api.js';
import axios from 'axios';
import '../styles/AdminPanel.css';


const MODE_ITEMS = {
  [APP_MODES.REFERRAL]: [
    'Solar Panels',
    'Battery Storage',
    'Roofing',
    'HVAC',
    'Permanent Lighting',
    'Windows',
    'EV Charger',
  ],
  [APP_MODES.ONBOARDING]: [
    '1099 Contractor',
    'W-2 Employee',
  ],
  [APP_MODES.INSTALL]: [
    'Solar Installation',
    'Battery Installation',
    'Roof Installation',
    'EV Charger Installation',
  ]
};

const FIELD_TYPES = [
  { value: 'text', label: 'Text Input', icon: '📝' },
  { value: 'textarea', label: 'Long Text', icon: '📄' },
  { value: 'number', label: 'Number', icon: '🔢' },
  { value: 'date', label: 'Date', icon: '📅' },
  { value: 'email', label: 'Email', icon: '📧' },
  { value: 'phone', label: 'Phone', icon: '📞' },
  { value: 'url', label: 'URL/Website', icon: '🔗' },
  { value: 'dropdown', label: 'Dropdown', icon: '▼' },
  { value: 'section', label: 'Section Header', icon: '📑' },
  { value: 'multiple-choice', label: 'Multiple Choice', icon: '☑️' },
  { value: 'file-upload', label: 'File Upload', icon: '📎' },
  { value: 'payment', label: 'Payment', icon: '💳' },
  { value: 'calendly', label: 'Calendly Booking', icon: '📅' },
];

function AdminPanel() {
  const [forms, setForms] = useState([]);
  const [selectedForm, setSelectedForm] = useState(null);
  const [editingForm, setEditingForm] = useState(null);
  const [showFormBuilder, setShowFormBuilder] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('forms');
  const [selectedMode, setSelectedMode] = useState(APP_MODES.REFERRAL);

  useEffect(() => {
    loadForms();
  }, []);

  const loadForms = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/referrals/admin/forms`);
      setForms(response.data.forms);
    } catch (error) {
      console.error('Error loading forms:', error);
    } finally {
      setLoading(false);
    }
  };

  const createNewForm = () => {
    setEditingForm({
      productName: '',
      product: '',
      mode: selectedMode,
      fields: [],
      metadata: [], // Initialize empty metadata array
    });
    setShowFormBuilder(true);
  };

  const editForm = (form) => {
    setEditingForm({ 
      ...form,
      metadata: form.metadata || [] // Ensure metadata exists
    });
    setShowFormBuilder(true);
  };

  const saveForm = async () => {
    try {
      console.log('Saving form:', editingForm); 
      if (editingForm._id) {
        await axios.put(`${API_URL}/api/referrals/admin/forms/${editingForm._id}`, editingForm);
      } else {
        await axios.post(`${API_URL}/api/referrals/admin/forms`, editingForm);
      }
      
      await loadForms();
      setShowFormBuilder(false);
      setEditingForm(null);
    } catch (error) {
      console.error('Error saving form:', error);
      alert('Error saving form. Please try again.');
    }
  };

  const deleteForm = async (formId) => {
    if (!confirm('Are you sure you want to delete this form?')) return;

    try {
      await axios.delete(`${API_URL}/api/referrals/admin/forms/${formId}`);
      await loadForms();
    } catch (error) {
      console.error('Error deleting form:', error);
    }
  };

  return (
    <div className="admin-panel">
      <div className="admin-header">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="header-content"
          >
            <div>
              <h1>🔧 Admin Panel</h1>
              <p>Manage referral forms and products</p>
            </div>
            <Link to="/" className="back-to-home-link">
              ← Back to Home
            </Link>
          </motion.div>
        </div>
      </div>
      <div className="container">
        <div className="admin-tabs">
          <button
            className={`tab-button ${activeTab === 'forms' ? 'active' : ''}`}
            onClick={() => setActiveTab('forms')}
          >
            📋 Forms
          </button>
          <button
            className={`tab-button ${activeTab === 'partners' ? 'active' : ''}`}
            onClick={() => setActiveTab('partners')}
          >
            🤝 Partners
          </button>
          <button
            className={`tab-button ${activeTab === 'contacts' ? 'active' : ''}`}
            onClick={() => setActiveTab('contacts')}
          >
            📞 Admin Contacts
          </button>
        </div>

        
          <div className="admin-content">
            {activeTab === 'forms' && (
              <>
              {!showFormBuilder && (
                  <div className="mode-selector" style={{ marginBottom: '20px' }}>
                    <div className="section-header" style={{ marginBottom: '10px' }}>
                      <h3>Select Mode</h3>
                    </div>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <button
                        className={`mode-button ${selectedMode === APP_MODES.REFERRAL ? 'active' : ''}`}
                        onClick={() => setSelectedMode(APP_MODES.REFERRAL)}
                        style={{
                          padding: '10px 20px',
                          border: selectedMode === APP_MODES.REFERRAL ? '2px solid #007bff' : '1px solid #ccc',
                          borderRadius: '8px',
                          background: selectedMode === APP_MODES.REFERRAL ? '#e7f3ff' : 'white',
                          cursor: 'pointer'
                        }}
                      >
                        📋 Referrals
                      </button>
                      <button
                        className={`mode-button ${selectedMode === APP_MODES.ONBOARDING ? 'active' : ''}`}
                        onClick={() => setSelectedMode(APP_MODES.ONBOARDING)}
                        style={{
                          padding: '10px 20px',
                          border: selectedMode === APP_MODES.ONBOARDING ? '2px solid #007bff' : '1px solid #ccc',
                          borderRadius: '8px',
                          background: selectedMode === APP_MODES.ONBOARDING ? '#e7f3ff' : 'white',
                          cursor: 'pointer'
                        }}
                      >
                        👔 Onboarding
                      </button>
                      <button
                        className={`mode-button ${selectedMode === APP_MODES.INSTALL ? 'active' : ''}`}
                        onClick={() => setSelectedMode(APP_MODES.INSTALL)}
                        style={{
                          padding: '10px 20px',
                          border: selectedMode === APP_MODES.INSTALL ? '2px solid #007bff' : '1px solid #ccc',
                          borderRadius: '8px',
                          background: selectedMode === APP_MODES.INSTALL ? '#e7f3ff' : 'white',
                          cursor: 'pointer'
                        }}
                      >
                        🔧 Installation
                      </button>
                    </div>
                  </div>
                )}
                {/* Forms List */}
                 {!showFormBuilder && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="forms-section"
                    >
                      <div className="section-header">
                        <h2>{selectedMode === APP_MODES.REFERRAL ? 'Product Forms' : selectedMode === APP_MODES.ONBOARDING ? 'Onboarding Forms' : 'Installation Forms'}</h2>
                        <motion.button
                          className="create-button"
                          onClick={createNewForm}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          + Create New Form
                        </motion.button>
                      </div>

                      {loading ? (
                        <div className="loading-state">
                          <div className="spinner"></div>
                          <p>Loading forms...</p>
                        </div>
                      ) : forms.filter(f => f.mode === selectedMode).length === 0 ? (
                        <div className="empty-state">
                          <div className="empty-icon">📋</div>
                          <h3>No forms yet</h3>
                          <p>Create your first form for this mode to get started</p>
                        </div>
                      ) : (
                        <div className="forms-grid">
                          {forms.filter(f => f.mode === selectedMode).map((form) => (
                            <motion.div
                              key={form._id}
                              className="form-card"
                              whileHover={{ scale: 1.02 }}
                              layout
                            >
                              <div className="form-card-header">
                                <h3>{form.productName}</h3>
                                <span className="field-count">{form.fields.length} fields</span>
                              </div>
                              <div className="form-card-body">
                                <p className="product-label">{form.product}</p>
                                {form.metadata && form.metadata.length > 0 && (
                                  <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
                                    🏷️ {form.metadata.length} metadata value{form.metadata.length !== 1 ? 's' : ''}
                                  </div>
                                )}
                                <div className="form-card-actions">
                                  <button
                                    className="action-button edit"
                                    onClick={() => editForm(form)}
                                  >
                                    ✏️ Edit
                                  </button>
                                  <button
                                    className="action-button delete"
                                    onClick={() => deleteForm(form._id)}
                                  >
                                    🗑️ Delete
                                  </button>
                                </div>
                              </div>
                            </motion.div>
                          ))}
                        </div>
                      )}
                    </motion.div>
                  )}
                {showFormBuilder && editingForm && (
                  <FormBuilder
                    form={editingForm}
                    onChange={setEditingForm}
                    onSave={saveForm}
                    onCancel={() => {
                      setShowFormBuilder(false);
                      setEditingForm(null);
                    }}
                  />
                )}
                </>
            )}
            {activeTab === 'partners' && (
                <PartnerManagement />
              )}
              {activeTab === 'contacts' && (
                  <AdminContactsManagement />
                )}
              </div>
            </div>
      </div>
      );
    }


function FormBuilder({ form, onChange, onSave, onCancel }) {
  const [showFieldModal, setShowFieldModal] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [fieldIndex, setFieldIndex] = useState(null);
  const [showMetadataModal, setShowMetadataModal] = useState(false);
  const [editingMetadata, setEditingMetadata] = useState(null);
  const [metadataIndex, setMetadataIndex] = useState(null);
  
  const availableItems = MODE_ITEMS[form.mode] || MODE_ITEMS[APP_MODES.REFERRAL];
  const itemLabel = form.mode === APP_MODES.ONBOARDING ? 'Employment Type' : 
                    form.mode === APP_MODES.INSTALL ? 'Installation Type' : 'Product';

  const addField = (type) => {
    const newField = {
      id: `field_${Date.now()}`,
      type,
      label: '',
      placeholder: '',
      required: false,
      options: type === 'multiple-choice' ? ['Option 1'] : [],
    };
    setEditingField(newField);
    setFieldIndex(null);
    setShowFieldModal(true);
  };

  const editField = (field, index) => {
    setEditingField({ ...field });
    setFieldIndex(index);
    setShowFieldModal(true);
  };

  const saveField = () => {
    if (!editingField.label.trim()) {
      alert('Please enter a field label');
      return;
    }

    const updatedFields = [...form.fields];
    if (fieldIndex !== null) {
      updatedFields[fieldIndex] = editingField;
    } else {
      updatedFields.push(editingField);
    }

    onChange({ ...form, fields: updatedFields });
    setShowFieldModal(false);
    setEditingField(null);
    setFieldIndex(null);
  };

  const deleteField = (index) => {
    const updatedFields = form.fields.filter((_, i) => i !== index);
    onChange({ ...form, fields: updatedFields });
  };

  const moveField = (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= form.fields.length) return;

    const updatedFields = [...form.fields];
    [updatedFields[index], updatedFields[newIndex]] = [updatedFields[newIndex], updatedFields[index]];
    onChange({ ...form, fields: updatedFields });
  };

  // Metadata functions
  const addMetadata = () => {
    setEditingMetadata({ key: '', value: '' });
    setMetadataIndex(null);
    setShowMetadataModal(true);
  };

  const editMetadata = (metadata, index) => {
    setEditingMetadata({ ...metadata });
    setMetadataIndex(index);
    setShowMetadataModal(true);
  };

  const saveMetadata = () => {
    if (!editingMetadata.key.trim()) {
      alert('Please enter a metadata key');
      return;
    }
    if (!editingMetadata.value.trim()) {
      alert('Please enter a metadata value');
      return;
    }

    const updatedMetadata = [...(form.metadata || [])];
    if (metadataIndex !== null) {
      updatedMetadata[metadataIndex] = editingMetadata;
    } else {
      updatedMetadata.push(editingMetadata);
    }

    onChange({ ...form, metadata: updatedMetadata });
    setShowMetadataModal(false);
    setEditingMetadata(null);
    setMetadataIndex(null);
  };

  const deleteMetadata = (index) => {
    const updatedMetadata = (form.metadata || []).filter((_, i) => i !== index);
    onChange({ ...form, metadata: updatedMetadata });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="form-builder"
    >
      <div className="builder-header">
        <h2>{form._id ? 'Edit Form' : 'Create New Form'}</h2>
        <div className="header-actions">
          <button className="cancel-button" onClick={onCancel}>
            Cancel
          </button>
          <button className="save-button" onClick={onSave}>
            💾 Save Form
          </button>
        </div>
      </div>

      <div className="builder-content">
        {/* Form Settings */}
        <div className="settings-section">
          <h3>Form Settings</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>{itemLabel} *</label>
              <select
                value={form.product}
                onChange={(e) => onChange({ ...form, product: e.target.value })}
                required
              >
                <option value="">Select {itemLabel.toLowerCase()}</option>
                {availableItems.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Form Title *</label>
              <input
                type="text"
                value={form.productName}
                onChange={(e) => onChange({ ...form, productName: e.target.value })}
                placeholder="e.g., Solar Installation Details"
                required
              />
            </div>
          </div>
          
          {/* NEW: Default form checkbox */}
          <div className="form-group" style={{ marginTop: '16px' }}>
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={form.isDefault || false}
                onChange={(e) => onChange({ ...form, isDefault: e.target.checked })}
              />
              <span>Set as default form for {form.product || 'this product'}</span>
            </label>
            <small style={{ color: '#666', marginTop: '4px', display: 'block' }}>
              This form will be pre-selected when creating referrals
            </small>
          </div>
        </div>

        {/* Metadata Section */}
        <div className="settings-section" style={{ marginTop: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <div>
              <h3>Form Metadata</h3>
              <p style={{ fontSize: '14px', color: '#666', marginTop: '4px' }}>
                Add fixed key-value pairs that will be saved with every submission
              </p>
            </div>
            <motion.button
              className="create-button"
              onClick={addMetadata}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              style={{ fontSize: '14px', padding: '8px 16px' }}
            >
              + Add Metadata
            </motion.button>
          </div>

          {form.metadata && form.metadata.length > 0 ? (
            <div className="metadata-list" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {form.metadata.map((metadata, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="metadata-item"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px 16px',
                    background: '#f8f9fa',
                    borderRadius: '8px',
                    border: '1px solid #e0e0e0'
                  }}
                >
                  <div style={{ flex: 1, display: 'flex', gap: '16px', alignItems: 'center' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Key</div>
                      <div style={{ fontWeight: '600', fontFamily: 'monospace' }}>{metadata.key}</div>
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Value</div>
                      <div style={{ fontFamily: 'monospace' }}>{metadata.value}</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => editMetadata(metadata, index)}
                      style={{
                        padding: '6px 12px',
                        background: 'white',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      ✏️
                    </button>
                    <button
                      onClick={() => deleteMetadata(index)}
                      style={{
                        padding: '6px 12px',
                        background: '#fee',
                        border: '1px solid #fcc',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        color: '#c00'
                      }}
                    >
                      🗑️
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div style={{ 
              padding: '24px', 
              textAlign: 'center', 
              color: '#999',
              background: '#f8f9fa',
              borderRadius: '8px',
              border: '1px dashed #ddd'
            }}>
              No metadata added. Metadata values will be automatically included with form submissions.
            </div>
          )}
        </div>

        {/* Field Types */}
        <div className="fields-section">
          <h3>Add Fields</h3>
          <div className="field-types-grid">
            {FIELD_TYPES.map((fieldType) => (
              <motion.button
                key={fieldType.value}
                className="field-type-button"
                onClick={() => addField(fieldType.value)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <span className="field-icon">{fieldType.icon}</span>
                <span>{fieldType.label}</span>
              </motion.button>
            ))}
          </div>
        </div>

        {/* Fields List */}
        <div className="fields-list-section">
          <h3>Form Fields ({form.fields.length})</h3>
          {form.fields.length === 0 ? (
            <div className="empty-fields">
              <p>No fields added yet. Add fields using the buttons above.</p>
            </div>
          ) : (
            <div className="fields-list">
              <AnimatePresence>
                {form.fields.map((field, index) => (
                  <motion.div
                    key={field.id}
                    className={`field-item ${field.type === 'section' ? 'section-field' : ''}`}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    layout
                  >
                    <div className="field-info">
                      <span className="field-number">{field.type === 'section' ? '📑' : index + 1}</span>
                      <div className="field-details">
                        <strong>{field.label || 'Untitled Field'}</strong>
                        <span className="field-type-badge">
                          {FIELD_TYPES.find(t => t.value === field.type)?.label}
                        </span>
                        {field.required && field.type !== 'section' && <span className="required-badge">Required</span>}
                      </div>
                    </div>
                    <div className="field-actions">
                      <button onClick={() => moveField(index, 'up')} disabled={index === 0}>
                        ↑
                      </button>
                      <button onClick={() => moveField(index, 'down')} disabled={index === form.fields.length - 1}>
                        ↓
                      </button>
                      <button onClick={() => editField(field, index)}>
                        ✏️
                      </button>
                      <button onClick={() => deleteField(index)} className="delete">
                        🗑️
                      </button>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          )}
        </div>
      </div>

      {/* Field Modal */}
      <AnimatePresence>
        {showFieldModal && (
          <FieldModal
            field={editingField}
            onChange={setEditingField}
            onSave={saveField}
            onCancel={() => {
              setShowFieldModal(false);
              setEditingField(null);
              setFieldIndex(null);
            }}
          />
        )}
      </AnimatePresence>

      {/* Metadata Modal */}
      <AnimatePresence>
        {showMetadataModal && (
          <MetadataModal
            metadata={editingMetadata}
            onChange={setEditingMetadata}
            onSave={saveMetadata}
            onCancel={() => {
              setShowMetadataModal(false);
              setEditingMetadata(null);
              setMetadataIndex(null);
            }}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
}

function MetadataModal({ metadata, onChange, onSave, onCancel }) {
  return (
    <motion.div
      className="modal-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onCancel}
    >
      <motion.div
        className="modal-content"
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '500px' }}
      >
        <h3>Configure Metadata</h3>
        
        <div className="modal-form">
          <div className="form-group">
            <label>Key *</label>
            <input
              type="text"
              value={metadata.key}
              onChange={(e) => onChange({ ...metadata, key: e.target.value })}
              placeholder="e.g., department, region, campaign_id"
              style={{ fontFamily: 'monospace' }}
            />
            <small style={{ color: '#666', marginTop: '4px', display: 'block' }}>
              Use lowercase with underscores (e.g., campaign_id)
            </small>
          </div>

          <div className="form-group">
            <label>Value *</label>
            <input
              type="text"
              value={metadata.value}
              onChange={(e) => onChange({ ...metadata, value: e.target.value })}
              placeholder="e.g., sales, west-coast, 2024-Q1"
              style={{ fontFamily: 'monospace' }}
            />
            <small style={{ color: '#666', marginTop: '4px', display: 'block' }}>
              This value will be saved with every form submission
            </small>
          </div>

          <div style={{ 
            padding: '12px', 
            background: '#e7f3ff', 
            borderRadius: '8px',
            border: '1px solid #b3d9ff',
            marginTop: '16px'
          }}>
            <div style={{ fontSize: '14px', color: '#0066cc', marginBottom: '4px', fontWeight: '600' }}>
              ℹ️ About Metadata
            </div>
            <div style={{ fontSize: '13px', color: '#333' }}>
              Metadata values are automatically included with form submissions and help you organize, filter, and track responses.
            </div>
          </div>
        </div>

        <div className="modal-actions">
          <button className="cancel-button" onClick={onCancel}>
            Cancel
          </button>
          <button className="save-button" onClick={onSave}>
            Save Metadata
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

function FieldModal({ field, onChange, onSave, onCancel }) {
  const addOption = () => {
    onChange({
      ...field,
      options: [...field.options, `Option ${field.options.length + 1}`]
    });
  };

  const updateOption = (index, value) => {
    const updatedOptions = [...field.options];
    updatedOptions[index] = value;
    onChange({ ...field, options: updatedOptions });
  };

  const removeOption = (index) => {
    onChange({
      ...field,
      options: field.options.filter((_, i) => i !== index)
    });
  };

  return (
    <motion.div
      className="modal-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onCancel}
    >
      <motion.div
        className="modal-content"
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3>Configure Field</h3>
        
        <div className="modal-form">
          <div className="form-group">
           <label>{field.type === 'section' ? 'Section Title *' : 'Field Label *'}</label>
            <input
              type="text"
              value={field.label}
              onChange={(e) => onChange({ ...field, label: e.target.value })}
              placeholder="e.g., Full Name"
            />
          </div>
          {field.type === 'date' && (
            <>
              <div className="form-group">
                <label>Minimum Date (Optional)</label>
                <input
                  type="date"
                  value={field.minDate || ''}
                  onChange={(e) => onChange({ ...field, minDate: e.target.value })}
                  className="form-input"
                />
                <small style={{ color: '#666', marginTop: '4px', display: 'block' }}>
                  Earliest date users can select
                </small>
              </div>
              <div className="form-group">
                <label>Maximum Date (Optional)</label>
                <input
                  type="date"
                  value={field.maxDate || ''}
                  onChange={(e) => onChange({ ...field, maxDate: e.target.value })}
                  className="form-input"
                />
                <small style={{ color: '#666', marginTop: '4px', display: 'block' }}>
                  Latest date users can select
                </small>
              </div>
            </>
          )}

          {/* Section Field - Description */}
          {field.type === 'section' && (
            <div className="form-group">
              <label>Section Description (Optional)</label>
              <textarea
                value={field.sectionDescription || ''}
                onChange={(e) => onChange({ ...field, sectionDescription: e.target.value })}
                placeholder="Brief description of this section"
                rows={2}
              />
            </div>
          )}
          {(field.type === 'text' || field.type === 'textarea' || field.type === 'number') && (
            <div className="form-group">
              <label>Placeholder</label>
              <input
                type="text"
                value={field.placeholder || ''}
                onChange={(e) => onChange({ ...field, placeholder: e.target.value })}
                placeholder="e.g., Enter your full name"
              />
            </div>
          )}
          {(field.type === 'email' || field.type === 'phone' || field.type === 'url') && (
              <div className="form-group">
                <label>Placeholder</label>
                <input
                  type="text"
                  value={field.placeholder || ''}
                  onChange={(e) => onChange({ ...field, placeholder: e.target.value })}
                  placeholder={
                    field.type === 'email' ? 'e.g., john@example.com' :
                    field.type === 'phone' ? 'e.g., (555) 123-4567' :
                    'e.g., https://example.com'
                  }
                />
              </div>
            )}

         {field.type === 'multiple-choice' && (
            <>
              <div className="form-group">
                <label>Selection Type</label>
                <select
                  value={field.selectionType || 'multiple'}
                  onChange={(e) => onChange({ ...field, selectionType: e.target.value })}
                  className="form-input"
                >
                  <option value="multiple">Multiple Select (Checkboxes)</option>
                  <option value="single">Single Select (Radio Buttons)</option>
                </select>
              </div>

              <div className="form-group">
                <label>Options</label>
                <div className="options-list">
                  {field.options.map((option, index) => (
                    <div key={index} className="option-item">
                      <input
                        type="text"
                        value={option}
                        onChange={(e) => updateOption(index, e.target.value)}
                      />
                      <button
                        type="button"
                        className="remove-option"
                        onClick={() => removeOption(index)}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
                <button type="button" className="add-option-button" onClick={addOption}>
                  + Add Option
                </button>
              </div>
            </>
          )}
          {field.type === 'file-upload' && (
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={field.allowMultiple || false}
                    onChange={(e) => onChange({ ...field, allowMultiple: e.target.checked })}
                  />
                  <span>Allow multiple files</span>
                </label>
                <small style={{ color: '#666', marginTop: '4px', display: 'block' }}>
                  Let users upload multiple files at once
                </small>
              </div>
            )}
          {field.type === 'dropdown' && (
              <div className="form-group">
                <label>Options</label>
                <div className="options-list">
                  {field.options.map((option, index) => (
                    <div key={index} className="option-item">
                      <input
                        type="text"
                        value={option}
                        onChange={(e) => updateOption(index, e.target.value)}
                      />
                      <button
                        type="button"
                        className="remove-option"
                        onClick={() => removeOption(index)}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
                <button type="button" className="add-option-button" onClick={addOption}>
                  + Add Option
                </button>
              </div>
            )}
          {field.type === 'payment' && (
              <>
              <div className="form-group">
              <label>Payment Type</label>
              <select
              value={field.paymentType || 'fixed'}
              onChange={(e) => onChange({ ...field, paymentType: e.target.value })}
              className="form-input"
              >
              <option value="fixed">Fixed Amount</option>
              <option value="custom">Customer Enters Amount</option>
              </select>
              </div>
              {field.paymentType === 'fixed' && (
                <div className="form-group">
                  <label>Amount (USD)</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '20px', fontWeight: 'bold' }}>$</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={(field.paymentAmount || 0) / 100}
                      onChange={(e) => onChange({ 
                        ...field, 
                        paymentAmount: Math.round(parseFloat(e.target.value) * 100)
                      })}
                      placeholder="50.00"
                      className="form-input"
                    />
                  </div>
                  <small style={{ color: '#666', marginTop: '4px', display: 'block' }}>
                    Amount will be charged in USD
                  </small>
                </div>
              )}
            
              <div className="form-group">
                <label>Payment Description</label>
                <input
                  type="text"
                  value={field.paymentDescription || ''}
                  onChange={(e) => onChange({ ...field, paymentDescription: e.target.value })}
                  placeholder="What is this payment for?"
                  className="form-input"
                />
                <small style={{ color: '#666', marginTop: '4px', display: 'block' }}>
                  This will appear on the customer's receipt
                </small>
              </div>
              </>
              )}

              {field.type === 'calendly' && (
                <div className="form-group">
                  <label>Calendly Event URL *</label>
                  <input
                    type="text"
                    value={field.calendlyUrl || ''}
                    onChange={(e) => onChange({ ...field, calendlyUrl: e.target.value })}
                    placeholder="https://calendly.com/your-username/event-name"
                    className="form-input"
                    required
                  />
                  <small style={{ color: '#666', marginTop: '4px', display: 'block' }}>
                    Go to Calendly → Event Types → Copy your event link
                  </small>
                  <small style={{ color: '#666', marginTop: '4px', display: 'block' }}>
                    Example: https://calendly.com/1solar/consultation
                  </small>
                </div>
              )}

           {field.type !== 'section' && (
            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={field.required}
                  onChange={(e) => onChange({ ...field, required: e.target.checked })}
                />
                <span>Required field</span>
              </label>
            </div>
          )}

          <div className="form-group">
            <label>Help Text (Optional)</label>
            <textarea
              value={field.description || ''}
              onChange={(e) => onChange({ ...field, description: e.target.value })}
              placeholder="Additional instructions for this field"
              rows={2}
            />
          </div>
        </div>

        <div className="modal-actions">
          <button className="cancel-button" onClick={onCancel}>
            Cancel
          </button>
          <button className="save-button" onClick={onSave}>
            Save Field
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default AdminPanel;